import {
  Button,
  Center,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  Input,
  Stack,
  Switch,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useRouter } from 'next/router';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';

import { DragAndDropImageUpload } from '@/components/DragAndDropImageUpload';
import { EPrepImagesSource } from '@/graphql-types/globalTypes';
import { logError } from '@/imports/logging/ClientLogger';
import { Card } from '@/imports/ui/chakra/components/Card';
import { REG_EXP } from '@/utilities';

import { useCreateCompanyForm } from './hooks/useCreateCompanyForm';

export type CreateCompanyFormValues = {
  name: string;
  contactEmail?: string;
  phoneNumber?: string;
  website?: string;
  streetAddress?: string;
  city?: string;
  state?: string;
  country?: string;
  zipCode?: string;
  isSponsor?: boolean;
  sponsorProfilePictureId?: string;
  sponsorBannerImageId: string;
};

type CreateCompanyFormProps = {
  modalButton?: boolean;
  onClose?: () => void;
  twoColumn?: boolean;
  submitRef?: React.MutableRefObject<(() => void) | null>;
}

export const CreateCompanyForm: React.FC<CreateCompanyFormProps> = ({
  modalButton= false,
  twoColumn=false,
  onClose,
  submitRef,
}) => {
  const router = useRouter();
  const toast = useToast();

  const { createCompany, isLoading } = useCreateCompanyForm();

  const methods = useForm<CreateCompanyFormValues>({
    defaultValues: {
      name: '',
      contactEmail: '',
      phoneNumber: '',
      website: '',
      streetAddress: '',
      city: '',
      state: '',
      country: '',
      zipCode: '',
      isSponsor: true,
      sponsorProfilePictureId: '',
      sponsorBannerImageId: '',
    },
  });

  const {
    handleSubmit,
    getFieldState,
    getValues,
    setValue,
    register,
    control,
    formState: { errors },
  } = methods;

  const onBlurContactEmail = () => {
    if (!getFieldState('contactEmail').isTouched) {
      setValue('contactEmail', getValues('contactEmail'));
    }
  };

  const onSubmit = handleSubmit(async (formData) => {
    try {
      const { data } = await createCompany({
        variables: {
          input: {
            name: formData.name,
            contactEmail: formData.contactEmail,
            phoneNumber: formData.phoneNumber,
            website: formData.website,
            streetAddress: formData.streetAddress,
            city: formData.city,
            state: formData.state,
            country: formData.country,
            zipCode: formData.zipCode,
            isSponsor: !!formData.isSponsor,
            sponsorProfilePictureId: formData.sponsorProfilePictureId,
            sponsorBannerImageId: formData.sponsorBannerImageId,
          },
        },
      });

      const res = data?.createOrganizationV2;

      if (res?.__typename === 'CreateOrganizationV2Success') {
        toast({
          description: 'Successfully created Organization.',
          status: 'success',
        });

       onClose?.();

        router.push(`/admin/companies/${res.organization._id}/details`);
      } else {
        const description =
          res?.__typename === 'CreateOrganizationV2DuplicateNameError'
            ? res?.message
            : res?.__typename ===
              'CreateOrganizationV2CreateNewAdminsDuplicateEmailsError'
            ? `${res?.message} (${res?.duplicateEmails?.join(', ')})`
            : res?.__typename ===
              'CreateOrganizationV2CreateNewAdminsInvalidInputsError'
            ? `${res?.message} (${res?.invalidEmails?.join(', ')})`
            : '';

        toast({
          description,
          status: 'error',
        });
        logError(Error(description));
      }
    } catch (err) {
      toast({
        description: 'Failed to create company.',
        status: 'error',
      });
      logError(err);
    }
  });

  React.useEffect(() => {
    if (!submitRef) {
      return undefined;
    }

    submitRef.current = onSubmit;
    return () => {
      submitRef.current = null;
    };
  }, [submitRef, onSubmit]);

  return (
    <>
      <Card p={twoColumn ? 0 : 6} boxShadow={twoColumn? 'none':'base'} w="full">
        <Stack spacing="6" as="form" noValidate>
          <Grid templateColumns={twoColumn ? { base: '1fr', md: '1fr 1fr' } : '1fr'} gap="6">
          <FormControl
            isRequired
            isInvalid={!!errors.name}
            isDisabled={isLoading}
          >
            <FormLabel>Organization Name</FormLabel>
            <Input
              placeholder="Organization name"
              {...register('name', {
                required: 'This is a required field',
              })}
            />
            <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.contactEmail} isDisabled={isLoading}>
            <FormLabel>Contact Email</FormLabel>
            <Input
              placeholder="name@domain.com"
              {...register('contactEmail', {
                onBlur: onBlurContactEmail,
                validate: {
                  email: (value) =>
                    !value ||
                    REG_EXP.EmailWithTLD.test(value) ||
                    'You must enter a valid email address',
                },
              })}
            />
            <FormErrorMessage>{errors.contactEmail?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isDisabled={isLoading}>
            <FormLabel>Phone Number</FormLabel>
            <Input placeholder="(123) 415-6789" {...register('phoneNumber')} />
          </FormControl>

          <FormControl isDisabled={isLoading}>
            <FormLabel>Website</FormLabel>
            <Input placeholder="www.domain.com" {...register('website')} />
          </FormControl>
        </Grid>
          <FormControl isDisabled={isLoading}>
            <FormLabel>Street Address</FormLabel>
            <Input
              placeholder="Street address"
              {...register('streetAddress')}
            />
          </FormControl>

          <Stack direction="row" spacing="4">
            <FormControl isDisabled={isLoading}>
              <FormLabel>City</FormLabel>
              <Input placeholder="City" {...register('city')} />
            </FormControl>

            <FormControl isDisabled={isLoading}>
              <FormLabel>State / Province / Region</FormLabel>
              <Input placeholder="State" {...register('state')} />
            </FormControl>
          </Stack>

          <Stack direction="row" spacing="4">
            <FormControl isDisabled={isLoading}>
              <FormLabel>Country</FormLabel>
              <Input placeholder="Country" {...register('country')} />
            </FormControl>

            <FormControl isDisabled={isLoading}>
              <FormLabel>Postal / Zip Code</FormLabel>
              <Input placeholder="Zip Code" {...register('zipCode')} />
            </FormControl>
          </Stack>

          <Divider />

          <Stack direction="row" alignItems="center">
            <Text textStyle="compact" flex="1">
              This company is a sponsor
            </Text>
            <Switch {...register('isSponsor')} isDisabled={isLoading} />
          </Stack>
        </Stack>
      </Card>

      <Card p={twoColumn ? 0 : 6} boxShadow={twoColumn? 'none':'base'} w="full">
      <Grid templateColumns={twoColumn ? { base: '1fr', md: '1fr 1fr' } : '1fr'} gap="6">
        <Stack>
        <Text textStyle="xlBodyStrong" mb="2">
          Banner Image
        </Text>

        <Controller
          name="sponsorBannerImageId"
          control={control}
          render={({ field: { onChange, value } }) => (
            <DragAndDropImageUpload
              onChange={onChange}
              value={value}
              aria-label="company banner"
              source={EPrepImagesSource.SPONSOR_BANNER_IMAGE}
              imageInput={{ ar: '8:3', w: 1600, fit: 'fill', fill: 'blur' }}
              imageProps={{ borderRadius: 'xl' }}
              nonDraftImageProxy={null}
              newView={twoColumn}
              imageSizeLarge={true}
            />
          )}
        />
      {!twoColumn && (
        <Center w="full" mt="6">
          <Text color="gray.500">The image size is 1600px by 600px</Text>
        </Center>
      )}
        </Stack>
        
        <Stack>
        <Text textStyle="xlBodyStrong" mb="2">
          Profile Picture
        </Text>

        <Controller
          name="sponsorProfilePictureId"
          control={control}
          render={({ field: { onChange, value } }) => (
            <DragAndDropImageUpload
              onChange={onChange}
              value={value}
              aria-label="company avatar"
              source={EPrepImagesSource.SPONSOR_PROFILE_PICTURE}
              imageInput={{
                w: 288,
                h: 288,
                fit: 'fill',
                fill: 'solid',
                fillColor: 'white',
              }}
              imageProps={{ h: '36', w: '36' }}
              nonDraftImageProxy={null}
              newView={twoColumn}
              imageSizeSmall={true}
            />
          )}
        />
        {!twoColumn && (
          <Center w="full" mt="6">
            <Text color="gray.500">The image size is 300px by 300px</Text>
          </Center>
        )}
        </Stack>
        </Grid>
      </Card>

      <Stack direction="row-reverse" spacing="4">
        {!modalButton && (<>
        <Button onClick={onSubmit} isLoading={isLoading}>
          Add Company
        </Button>
        <Button
          isDisabled={isLoading}
          onClick={() => router.push('/admin/companies')}
          variant="outline"
          colorScheme="gray"
        >
          Cancel
        </Button></>)}

      {/* {modalButton? <>
        <Button
          isDisabled={isLoading}
          onClick={onClose}
          variant="outline"
          colorScheme="gray"
        >
          Cancel
        </Button>
        </>: <>
        <Button
          isDisabled={isLoading}
          onClick={() => router.push('/admin/companies')}
          variant="outline"
          colorScheme="gray"
        >
          Cancel
        </Button></>} */}
        
      </Stack>
    </>
  );
};
